import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { generatePath, Outlet, useLocation, useNavigate, useParams } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { GaiaMap } from "~/modules/map/components/GaiaMap";

import { MapDatepicker } from "../../components/MapDatepicker";
import { PlannerDraftStatusDrawer } from "../../components/PlannerDraftStatusDrawer";
import { PLANNERV2_DEFAULT_BOUNDS } from "../../constants/map";
import { loadGaiaPlannerV2Images } from "../../util/loadPlannerV2Images";
import usePlannerV2AreasGetWithPendingChanges from "./hooks/usePlannerV2ScreenGetData";
import { PlannerV2ContextType } from "./types";

export function PlannerV2Layout() {
    const { date: dateString } = useParams();
    if (!dateString) throw new Error("Parameter `date` not provided");
    const currentDate = parseStringToDate(dateString);

    const navigate = useNavigate();
    const location = useLocation();

    const { totalDrafts } = usePlannerV2AreasGetWithPendingChanges();

    const onDateChange = useCallback(
        (newDate: Date) => {
            const newDateString = format(newDate, "yyyy-MM-dd");
            const currentDateString = format(currentDate, "yyyy-MM-dd");
            // we need to do a string replace here because we do not know any child route paths
            const pathname = location.pathname.replace(`/planner/${currentDateString}`, `/planner/${newDateString}`);
            navigate({ pathname });
        },
        [currentDate, location, navigate],
    );

    const plannerSharedLeftPanel = useMemo(() => {
        return <MapDatepicker currentDate={currentDate} onDateChange={onDateChange} />;
    }, [currentDate, onDateChange]);

    const plannerSharedBottomRightPanel = useMemo(() => {
        return (
            <PlannerDraftStatusDrawer
                totalDrafts={totalDrafts}
                openReviewPanelHref={generatePath("/planner/:date/review-schedule", { date: dateString })}
            />
        );
    }, [totalDrafts, dateString]);

    return (
        <GaiaMap sx={{ width: "100%", height: "100%" }} bounds={PLANNERV2_DEFAULT_BOUNDS} onMapLoaded={loadGaiaPlannerV2Images}>
            <Outlet context={{ plannerSharedLeftPanel, plannerSharedBottomRightPanel } satisfies PlannerV2ContextType} />
        </GaiaMap>
    );
}
